import React, { useState, useEffect } from 'react';
import { useLoaderData, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { FiArrowLeft, FiUser, FiMail, FiPhone, FiCalendar, FiMapPin, FiBook, FiBookOpen, FiActivity, FiClock, FiCheck, FiX, FiShoppingBag, FiTrash2, FiAlertTriangle } from 'react-icons/fi';
import OrderHistory from './OrderHistory'; // Import the new OrderHistory component

// Helper function to format dates
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
};

// Loader function to fetch student data
export const studentLoader = async ({ params }) => {
  try {
    const response = await axios.get(`https://apissc.sainikschoolcadet.com/api/Students/getbyid/${params.studentId}`);
    return response.data;
  } catch (err) {
    console.error('Error fetching student data:', err);
    throw new Error('Failed to fetch student data');
  }
};

// Follow-up status component - handles its own data fetching with error handling
const FollowUpStatus = ({ activityId, refreshTrigger }) => {
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  useEffect(() => {
    const fetchFollowUps = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`https://apissc.sainikschoolcadet.com/api/activity-followup/activity/${activityId}`);
        // If data exists and is valid
        if (response.data && Array.isArray(response.data)) {
          setFollowUps(response.data);
        } else if (response.data && !Array.isArray(response.data)) {
          // Handle case where a single object is returned instead of an array
          setFollowUps([response.data]);
        } else {
          setFollowUps([]);
        }
      } catch (error) {
        // Handle 404 or other errors gracefully - just means no follow-ups exist
        console.log(`No follow-ups found for activity ${activityId}`);
        setFollowUps([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchFollowUps();
  }, [activityId, refreshTrigger]); // Add refreshTrigger as a dependency
  
  // Function to toggle follow-up status
  const toggleFollowUpStatus = async (followUpId, currentStatus) => {
    setUpdatingStatus(true);
    
    // Determine the new status (opposite of current status)
    const newStatus = currentStatus === 'complete' ? 'incomplete' : 'complete';
    
    try {
      // Call the API to update the follow-up status
      await axios.put(`https://apissc.sainikschoolcadet.com/api/activity-followup/status/${followUpId}`, {
        status: newStatus
      });
      
      // Update the local state to reflect the change
      setFollowUps(prevFollowUps => 
        prevFollowUps.map(followUp => 
          followUp.id === followUpId 
            ? { ...followUp, status: newStatus } 
            : followUp
        )
      );
    } catch (error) {
      console.error(`Error updating follow-up status:`, error);
      alert('Failed to update follow-up status. Please try again.');
    } finally {
      setUpdatingStatus(false);
    }
  };
  
  if (loading) {
    return <p className="text-sm text-gray-500 mt-2">Loading follow-ups...</p>;
  }
  
  if (followUps.length === 0) {
    return (
      <p className="text-sm text-gray-500 mt-2 flex items-center">
        <FiX className="text-red-500 mr-1" /> No follow-ups scheduled
      </p>
    );
  }
  
  return (
    <div className="mt-2">
      {followUps.map((followUp, index) => (
        <div key={index} className="text-sm flex items-center text-gray-700 mb-1">
          <button
            className="flex items-center mr-2 focus:outline-none"
            onClick={() => toggleFollowUpStatus(followUp.id, followUp.status)}
            disabled={updatingStatus}
            title={followUp.status === 'complete' ? 'Mark as incomplete' : 'Mark as complete'}
          >
            {followUp.status === 'complete' ? (
              <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center text-white">
                <FiCheck size={12} />
              </div>
            ) : (
              <div className="w-5 h-5 rounded-full border-2 border-gray-300 hover:border-green-500" />
            )}
          </button>
          <div className={followUp.status === 'complete' ? 'line-through text-gray-500' : ''}>
            Follow-up: {formatDate(followUp.followup_date)}
            {followUp.notes && <span className="ml-2">- {followUp.notes}</span>}
          </div>
        </div>
      ))}
    </div>
  );
};

const ViewStudent = () => {
  const navigate = useNavigate();
  const student = useLoaderData();
  const { studentId } = useParams();
  
  // Activity state
  const [leadStatus, setLeadStatus] = useState('');
  const [leadLabel, setLeadLabel] = useState('');
  const [activityNotes, setActivityNotes] = useState('');
  const [activities, setActivities] = useState([]);
  const [isSubmittingActivity, setIsSubmittingActivity] = useState(false);
  
  // Follow-up state
  const [selectedActivityId, setSelectedActivityId] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpNotes, setFollowUpNotes] = useState('');
  const [isSubmittingFollowUp, setIsSubmittingFollowUp] = useState(false);
  const [refreshFollowUpTrigger, setRefreshFollowUpTrigger] = useState(0);
  
  // Active tab state for mobile view
  const [activeTab, setActiveTab] = useState('profile'); // 'profile', 'activity', 'orders'

  // Delete Student state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmationInput, setDeleteConfirmationInput] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const handleDeleteStudent = async () => {
    if (deleteConfirmationInput !== 'DELETE') {
      return;
    }
    setIsDeleting(true);
    setDeleteError('');
    try {
      const userId = localStorage.getItem('user_id');
      const email = localStorage.getItem('email');
      const role = localStorage.getItem('role');

      const response = await axios.delete(`https://apissc.sainikschoolcadet.com/api/Students/deleteStudent/${studentId}`, {
        headers: {
          'x-user-id': userId,
          'x-user-email': email,
          'x-user-role': role,
        }
      });

      alert(response.data?.message || 'Student account and all associated records permanently deleted successfully.');
      navigate('/Student', { replace: true });
    } catch (err) {
      console.error('Failed to delete student:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Failed to delete student account. Please try again.';
      setDeleteError(errorMessage);
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Fetch student activities when studentId is available
  useEffect(() => {
    if (studentId) {
      fetchActivities();
    }
  }, [studentId]);
  
  const fetchActivities = async () => {
    try {
      const response = await axios.get(`https://apissc.sainikschoolcadet.com/api/student-activity/student/${studentId}`);
      setActivities(response.data);
      
      // Increment the refresh trigger to cause follow-up components to refresh
      setRefreshFollowUpTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };
  
  const handleActivitySubmit = async (e) => {
    e.preventDefault();
    
    if (!leadStatus) {
      alert('Please select a lead status');
      return;
    }
    
    setIsSubmittingActivity(true);
    
    try {
      await axios.post('https://apissc.sainikschoolcadet.com/api/student-activity', {
        s_id: parseInt(studentId),
        lead_status: leadStatus,
        lead_label: leadLabel,
        notes: activityNotes
      });
      
      // Reset form and fetch updated activities
      setLeadStatus('');
      setLeadLabel('');
      setActivityNotes('');
      fetchActivities();
      alert('Activity added successfully');
    } catch (error) {
      console.error('Error adding activity:', error);
      alert('Failed to add activity. Please try again.');
    } finally {
      setIsSubmittingActivity(false);
    }
  };
  
  const handleFollowUpSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedActivityId) {
      alert('Please select an activity');
      return;
    }
    
    if (!followUpDate) {
      alert('Please select a follow-up date');
      return;
    }
    
    setIsSubmittingFollowUp(true);
    
    try {
      await axios.post('https://apissc.sainikschoolcadet.com/api/activity-followup', {
        activity_id: selectedActivityId,
        followup_date: followUpDate,
        notes: followUpNotes,
        status: 'incomplete' // Default status for new follow-up
      });
      
      // Reset form
      setSelectedActivityId('');
      setFollowUpDate('');
      setFollowUpNotes('');
      alert('Follow-up added successfully');
      
      // Trigger a refresh to update the activity list
      fetchActivities();
      
    } catch (error) {
      console.error('Error adding follow-up:', error);
      alert('Failed to add follow-up. Please try again.');
    } finally {
      setIsSubmittingFollowUp(false);
    }
  };

  if (!student) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-700">Student not found</p>
          
        </div>
      </div>
    );
  }

  // Mobile tab navigation
  const renderMobileTabNav = () => (
    <div className="lg:hidden flex border-b border-gray-200 mb-4">
      <button
        className={`flex-1 py-2 px-2 font-medium text-sm ${
          activeTab === 'profile'
            ? 'border-b-2 border-indigo-500 text-indigo-600'
            : 'text-gray-500 hover:text-gray-700'
        }`}
        onClick={() => setActiveTab('profile')}
      >
        <div className="flex items-center justify-center">
          <FiUser className="mr-1" />
          Profile
        </div>
      </button>
      <button
        className={`flex-1 py-2 px-2 font-medium text-sm ${
          activeTab === 'activity'
            ? 'border-b-2 border-indigo-500 text-indigo-600'
            : 'text-gray-500 hover:text-gray-700'
        }`}
        onClick={() => setActiveTab('activity')}
      >
        <div className="flex items-center justify-center">
          <FiActivity className="mr-1" />
          Activities
        </div>
      </button>
      <button
        className={`flex-1 py-2 px-2 font-medium text-sm ${
          activeTab === 'orders'
            ? 'border-b-2 border-indigo-500 text-indigo-600'
            : 'text-gray-500 hover:text-gray-700'
        }`}
        onClick={() => setActiveTab('orders')}
      >
        <div className="flex items-center justify-center">
          <FiShoppingBag className="mr-1" />
          Orders
        </div>
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {/* Common header with student name and back button */}
      <div className="bg-indigo-600 px-6 py-5 rounded-t-lg shadow-lg mb-4 text-white">
        <div className="flex flex-wrap justify-between items-center gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/Student')}
              className="p-2 bg-indigo-700/60 hover:bg-indigo-700 rounded-full transition-colors"
              title="Back to Students"
            >
              <FiArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold">{student.f_name} {student.l_name}</h1>
              <p className="text-xs text-indigo-200 mt-0.5">Student ID: #{student.s_id || studentId}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setDeleteConfirmationInput('');
                setDeleteError('');
                setShowDeleteModal(true);
              }}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-md shadow-sm transition-colors flex items-center gap-2 text-sm"
            >
              <FiTrash2 size={16} />
              Delete Student
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Tab Navigation */}
      {renderMobileTabNav()}
      
      {/* Main three-column layout for desktop, conditional display for mobile */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Left Column: Profile Information */}
        {(activeTab === 'profile' || window.innerWidth >= 1024) && (
          <div className="w-full lg:w-1/3">
            <div className="bg-white shadow-lg rounded-lg overflow-hidden mb-4">
              <div className="p-6">
                {/* Personal Information Section */}
                <div className="bg-gray-50 p-6 rounded-lg mb-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">Personal Information</h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <FiUser className="text-indigo-500 mt-1 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Full Name</p>
                        <p className="font-medium text-gray-800">{student.f_name} {student.l_name}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <FiMail className="text-indigo-500 mt-1 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="font-medium text-gray-800">{student.email}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <FiPhone className="text-indigo-500 mt-1 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Phone Number</p>
                        <p className="font-medium text-gray-800">{student.phonenumber}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <FiUser className="text-indigo-500 mt-1 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Gender</p>
                        <p className="font-medium text-gray-800">{student.gender}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <FiCalendar className="text-indigo-500 mt-1 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Date of Birth</p>
                        <p className="font-medium text-gray-800">{formatDate(student.dob)}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Location & Academic Section */}
                <div className="bg-gray-50 p-6 rounded-lg mb-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">Location & Academic</h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <FiMapPin className="text-indigo-500 mt-1 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">State</p>
                        <p className="font-medium text-gray-800">{student.state}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <FiMapPin className="text-indigo-500 mt-1 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">City</p>
                        <p className="font-medium text-gray-800">{student.city}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <FiBook className="text-indigo-500 mt-1 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Class Entry</p>
                        <p className="font-medium text-gray-800">Class {student.c_entry}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <FiBookOpen className="text-indigo-500 mt-1 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">School Choice</p>
                        <p className="font-medium text-gray-800">{student.c_school}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Registration Information */}
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">Registration Information</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Registered On</p>
                      <p className="font-medium text-gray-800">{formatDate(student.created_at)}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-500">Last Updated</p>
                      <p className="font-medium text-gray-800">{formatDate(student.updated_at)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Middle Column: Activity and Follow-Up */}
        {(activeTab === 'activity' || window.innerWidth >= 1024) && (
          <div className="w-full lg:w-1/3">
            <div className="bg-white shadow-lg rounded-lg overflow-hidden mb-4">
              <div className="p-6">
                {/* Activity History Section */}
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Activity History</h2>
                  
                  {activities.length === 0 ? (
                    <div className="text-center text-gray-500 p-4 bg-gray-50 rounded">
                      <p>No activities found for this student.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {activities.map((activity) => (
                        <div key={activity.id} className="border-l-4 border-indigo-500 pl-4 py-3 bg-gray-50 rounded">
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="inline-block bg-indigo-100 text-indigo-800 px-2 py-1 rounded text-sm font-medium">
                                {activity.lead_status}
                              </span>
                              {activity.lead_label && (
                                <span className="ml-2 inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-sm">
                                  {activity.lead_label}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">{formatDate(activity.created_at)}</p>
                          </div>
                          
                          {activity.notes && (
                            <p className="text-sm text-gray-700 mt-2">{activity.notes}</p>
                          )}
                          
                          <FollowUpStatus 
                            activityId={activity.id} 
                            refreshTrigger={refreshFollowUpTrigger} 
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Activity Form */}
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-indigo-600 mb-4 border-b pb-2 flex items-center">
                    <FiActivity className="mr-2" />
                    Add Student Activity
                  </h2>
                  
                  <form onSubmit={handleActivitySubmit} className="bg-gray-50 p-4 rounded-lg">
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Lead Status*</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        value={leadStatus}
                        onChange={(e) => setLeadStatus(e.target.value)}
                        required
                      >
                        <option value="">Select Status</option>
                        <option value="Call Not Answered">Call Not Answered</option>
                        <option value="Call Answered">Call Answered</option>
                        <option value="Interested">Interested</option>
                        <option value="Online Demo Taken">Online Demo Taken</option>
                        <option value="Gurgaon Campus Visited">Gurgaon Campus Visited</option>
                        <option value="Online Admission Taken">Online Admission Taken</option>
                        <option value="Gurgaon Admission Taken">Gurgaon Admission Taken</option>
                        <option value="Not Interested">Not Interested</option>
                      </select>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Lead Label</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        value={leadLabel}
                        onChange={(e) => setLeadLabel(e.target.value)}
                      >
                        <option value="">Select Label</option>
                        <option value="SSQ24">SSQ24</option>
                        <option value="SSQ25">SSQ25</option>
                        <option value="SSQ26">SSQ26</option>
                        <option value="SSQ27">SSQ27</option>
                      </select>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                      <textarea
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        rows="3"
                        value={activityNotes}
                        onChange={(e) => setActivityNotes(e.target.value)}
                        placeholder="Add notes about the interaction..."
                      ></textarea>
                    </div>
                    
                    <div>
                      <button
                        type="submit"
                        className="w-full py-2 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
                        disabled={isSubmittingActivity}
                      >
                        {isSubmittingActivity ? 'Adding...' : 'Add Activity'}
                      </button>
                    </div>
                  </form>
                </div>
                
                {/* Follow-Up Form */}
                <div>
                  <h2 className="text-lg font-semibold text-green-600 mb-4 border-b pb-2 flex items-center">
                    <FiClock className="mr-2" />
                    Add Follow-Up
                  </h2>
                  
                  <form onSubmit={handleFollowUpSubmit} className="bg-gray-50 p-4 rounded-lg">
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Select Activity*</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        value={selectedActivityId}
                        onChange={(e) => setSelectedActivityId(e.target.value)}
                        required
                      >
                        <option value="">Select Activity</option>
                        {activities.map((activity) => (
                          <option key={activity.id} value={activity.id}>
                            {activity.lead_status} - {formatDate(activity.created_at)}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Follow-Up Date*</label>
                      <input
                        type="date"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        value={followUpDate}
                        onChange={(e) => setFollowUpDate(e.target.value)}
                        required
                      />
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Follow-Up Notes</label>
                      <textarea
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        rows="3"
                        value={followUpNotes}
                        onChange={(e) => setFollowUpNotes(e.target.value)}
                        placeholder="Add notes for the follow-up..."
                      ></textarea>
                    </div>
                    
                    <div>
                      <button
                        type="submit"
                        className="w-full py-2 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
                        disabled={isSubmittingFollowUp}
                      >
                        {isSubmittingFollowUp ? 'Adding...' : 'Add Follow-Up'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Right Column: Order History - New Section */}
        {(activeTab === 'orders' || window.innerWidth >= 1024) && (
          <div className="w-full lg:w-1/3">
            {/* Render the OrderHistory component */}
            <OrderHistory studentId={studentId} />
          </div>
        )}
      </div>

      {/* Danger Zone Section */}
      <div className="mt-8 bg-red-50 border border-red-200 rounded-lg p-6 shadow-sm">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-red-700 font-bold text-lg mb-1">
              <FiAlertTriangle className="text-red-600 text-xl" />
              <span>Danger Zone: Permanent Account Deletion</span>
            </div>
            <p className="text-sm text-red-600 max-w-2xl">
              Permanently delete this student account (ID: #{student.s_id || studentId}) and all associated records (batch enrollments, test scores, content access, lead activities, follow-ups, and orders). This action is <strong>permanent</strong> and <strong>cannot be undone</strong>.
            </p>
          </div>
          <button
            onClick={() => {
              setDeleteConfirmationInput('');
              setDeleteError('');
              setShowDeleteModal(true);
            }}
            className="px-5 py-2.5 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-semibold rounded-md shadow-sm transition-colors flex items-center gap-2 whitespace-nowrap text-sm"
          >
            <FiTrash2 size={16} />
            Delete Student Account
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden border border-red-200 animate-in fade-in zoom-in duration-150">
            {/* Modal Header */}
            <div className="bg-red-600 px-6 py-4 text-white flex items-center gap-3">
              <div className="p-2 bg-red-700 rounded-full">
                <FiAlertTriangle size={22} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold">Confirm Permanent Deletion</h3>
                <p className="text-xs text-red-100">This action cannot be undone</p>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded-r-md">
                <p className="text-sm text-red-900 font-medium leading-relaxed">
                  You are about to <strong>permanently delete</strong> the following student account and all linked database records:
                </p>
                <div className="mt-3 bg-white p-3 rounded border border-red-200 text-sm space-y-1">
                  <p><strong className="text-gray-700">Name:</strong> <span className="text-gray-900 font-semibold">{student.f_name} {student.l_name || ''}</span></p>
                  <p><strong className="text-gray-700">Student ID:</strong> <span className="text-gray-900 font-mono font-semibold">#{student.s_id || studentId}</span></p>
                  <p><strong className="text-gray-700">Phone:</strong> <span className="text-gray-900 font-mono">{student.phonenumber || 'N/A'}</span></p>
                  <p><strong className="text-gray-700">Email:</strong> <span className="text-gray-900">{student.email || 'N/A'}</span></p>
                </div>
              </div>

              <div className="text-sm text-gray-700 space-y-2">
                <p className="font-semibold text-gray-900">What will be deleted:</p>
                <ul className="list-disc list-inside text-xs text-gray-600 space-y-1 pl-1">
                  <li>Student account profile and authentication record</li>
                  <li>All batch enrollments and class memberships</li>
                  <li>All test attempts, answers, and scores</li>
                  <li>Course and content access entitlements</li>
                  <li>Lead activities, follow-ups, and callback requests</li>
                  <li>Order and cart entries</li>
                  <li>Push notification device tokens</li>
                </ul>
              </div>

              {deleteError && (
                <div className="p-3 bg-red-100 border border-red-300 text-red-800 text-sm rounded">
                  {deleteError}
                </div>
              )}

              <div className="pt-2">
                <label className="block text-sm font-bold text-gray-800 mb-1">
                  To confirm, type <span className="text-red-600 font-mono tracking-wider">DELETE</span> in all caps:
                </label>
                <input
                  type="text"
                  value={deleteConfirmationInput}
                  onChange={(e) => setDeleteConfirmationInput(e.target.value)}
                  placeholder="DELETE"
                  disabled={isDeleting}
                  className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 font-mono tracking-widest text-center text-lg font-bold"
                  autoFocus
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-100">
              <button
                type="button"
                onClick={() => {
                  if (!isDeleting) {
                    setShowDeleteModal(false);
                    setDeleteConfirmationInput('');
                    setDeleteError('');
                  }
                }}
                disabled={isDeleting}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-100 transition-colors text-sm disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteStudent}
                disabled={deleteConfirmationInput !== 'DELETE' || isDeleting}
                className={`px-5 py-2 text-white font-semibold rounded-md shadow-sm transition-all text-sm flex items-center gap-2 ${
                  deleteConfirmationInput === 'DELETE' && !isDeleting
                    ? 'bg-red-600 hover:bg-red-700 active:bg-red-800 cursor-pointer'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isDeleting ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                    </svg>
                    <span>Deleting Permanently...</span>
                  </>
                ) : (
                  <>
                    <FiTrash2 size={16} />
                    <span>Permanently Delete Student</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewStudent;